import {
  animate,
  ImageScaleView,
  ImageView,
  View,
  ViewOpts,
} from '@play-co/timestep-core/ui';
import {
  DecorationItem,
  DecorationItemID,
  DecorationRoomID,
  DecorationSlot,
  DecorationThemeID,
  DecorationTierID,
} from 'src/replicant/decoration/decoration.ruleset';
import Scroll from '../components/shared/Scroll';
import { loader, PubSub } from '@play-co/timestep-core';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { Feature } from 'src/replicant/analytics';
import {
  getAvailableDecorationsForSlot,
  getDecorationPath,
  isDecorationPurchased,
} from './helpers';
import { animDuration, parseAmount } from 'src/lib/utils';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import i18n from 'src/lib/i18n/i18n';
import bitmapFonts from 'src/lib/bitmapFonts';
import LazyImageCoverView from 'src/lib/ui/components/LazyImageCoverView';
import { assertNever } from 'src/replicant/utils';
import TierView from 'src/game/catalog/TierView';
import { group } from 'src/replicant/utils/array';
import StateObserver from 'src/StateObserver';
import { DecorationThemeVariant } from '../../replicant/data/DecorationItems.schema';

/**
 * Type for list
 */
export type DecorationListItem = DecorationItem & {
  selected?: boolean;
  default?: boolean;
  stars?: number;
  isNew?: boolean;
  tierId?: DecorationTierID;
};

/**
 * Choose decoration for slot based on roomID and slot ID
 */
export default class DecorationSelectItemView extends PubSub {
  // Root view
  private view: View;

  // Items list scroll view
  private itemsList: Scroll<DecorationListItem, DecorationItemView>;

  // Shadow view
  private shadow: View;

  // Animation in progress
  private animating = false;

  // Panel is already on screen
  public isOpen = false;

  // Save current data reference for reflecting selected items
  private data!: DecorationListItem[];

  /**
   * Constructor
   */
  constructor(opts: ViewOpts) {
    super();

    this.view = new View(opts);

    // Shadow view
    this.shadow = new ImageScaleView({
      superview: this.view,
      image: 'assets/ui/decoration/frames/gradient.png',
      canHandleEvents: false,
      scaleMethod: '3slice',
      sourceSlices: {
        horizontal: { left: 1, right: 1 },
      },
      width: opts.width,
      height: 360,
      y: -120,
      visible: false,
    });

    this.itemsList = new Scroll<DecorationListItem, DecorationItemView>({
      superview: this.view,
      createItem: this.createItem.bind(this),
      setData: this.setItemData.bind(this),
      showBg: false,
      rect: {
        x: 0,
        y: 0,
        width: opts.width!,
        height: 235,
      },
      horizontal: true,
      firstItemOffset: 0,
      recycle: true,
    });

    this.itemsList.getView().hide();
  }

  /**
   * Show panel with available items
   */
  public async open(
    slot: DecorationSlot,
    room: DecorationRoomID,
    selected: DecorationListItem | null,
    defaultItemImage: string | undefined,
  ) {
    // Block animation
    if (this.animating) return;
    this.animating = true;

    try {
      await this.openSequence(slot, room, selected, defaultItemImage);
    } finally {
      // Release animation block
      this.isOpen = true;
      this.animating = false;
    }
  }

  /**
   * Open items logic
   */
  private async openSequence(
    slot: DecorationSlot,
    room: DecorationRoomID,
    selected: DecorationListItem | null,
    defaultItemImage: string | undefined,
  ) {
    // Hide panel if it open
    if (this.isOpen) {
      await this.animateSlideDown();
    }

    // Get all available decorations
    // Create copy iof data for modifications and also it cast DecorationItem to DecorationListItem
    const decorations = getAvailableDecorationsForSlot(slot, room).map(
      (item) => {
        return {
          ...item,
          selected: false,
          default: false,
        } as DecorationListItem;
      },
    );

    // Mark selected item
    if (selected) {
      const selectedItem = decorations.find((item) => item.id === selected.id);
      if (selectedItem) selectedItem.selected = true;
    }

    // Create default decoration item
    const defaultItem: DecorationListItem = {
      id: 'default' as DecorationItemID,
      room,
      theme: 'default' as DecorationThemeID,
      color: 'default' as DecorationThemeVariant,
      slot,
      image: defaultItemImage,
      priority: 0.5,
      currency: 'bPoints',
      price: 0,
      selected: selected ? false : true,
      name_en: 'Default',
      name_jp: 'デフォルト',
      max_tier: 1,
      stars: 2,
      tierId: 'default' as DecorationTierID,
    };

    // Add default decoration
    decorations.push(defaultItem);

    // Group items.
    const { user } = StateObserver.getState();
    const groups = group(decorations, (x) => {
      // No stars group.
      if (!x.stars) return '0';

      // Equipped group.
      if ((selected?.id ?? defaultItem.id) === x.id) return '1';

      // Rest group.
      return '3';
    });

    // Sort each group. And merge groups.
    this.data = Object.keys(groups)
      .sort((a, b) => Number.parseInt(a) - Number.parseInt(b))
      .map((key) => groups[key])
      .reduce((acc, group) => {
        // Sort groups based on New > Tier > Theme
        group.sort((a, b) => {
          // Sort New items first
          if (a.isNew && !b.isNew) {
            return -1;
          }
          if (!a.isNew && b.isNew) {
            return 1;
          }

          const aStars = a.stars ?? Number.MAX_SAFE_INTEGER;
          const bStars = b.stars ?? Number.MAX_SAFE_INTEGER;

          // Sort by theme DESC. If stars are equal.
          if (aStars === bStars) {
            return -a.theme.localeCompare(b.theme);
          }

          // Sort by stars.
          return bStars - aStars;
        });
        // Add groups to the accumulator.
        acc.push(...group);
        return acc;
      }, []);

    // Populate list
    this.itemsList.setItems(this.data);

    // Show list with animation
    if (this.isOpen) {
      await this.animateSlideUp();
    } else {
      await Promise.all([this.animateSlideUp(), this.animateShowShadow()]);
    }
  }

  /**
   * Hide select items panel
   */
  public async close() {
    // Block for animation
    if (this.animating) return;
    if (!this.isOpen) return;
    this.animating = true;

    // Show hide animation
    await Promise.all([this.animateHideShadow(), this.animateSlideDown()]);

    // Release animation block
    this.isOpen = false;
    this.animating = false;
  }

  /**
   * Create scroll list element
   */
  private createItem(
    superview: View,
    index: number,
    data: DecorationListItem,
  ): DecorationItemView {
    const view = new DecorationItemView(
      { superview, width: 237, height: 235 },
      data,
      (item, view) => this.selectItem(item, view),
    );

    return view;
  }

  /**
   * Set item data
   */
  private setItemData(
    _: number,
    item: DecorationListItem,
    view: DecorationItemView,
  ) {
    view.setData(item);
  }

  /**
   * Select view function
   */
  private selectItem(item: DecorationListItem, view: DecorationItemView) {
    const image = getDecorationPath(item.image);

    // Block not loaded images from selection
    // User could select items only when images finish loading
    if (image && item.image && !loader.cache[image]) {
      return;
    }

    const list = this.itemsList
      .getView()
      .getContentView()
      .getSubviews() as DecorationItemView[];

    // Immediately show change on ui
    list.forEach((item) => {
      item.setActiveIcon(false);
    });
    view.setActiveIcon(true);

    // Reflect data change in out model
    // It's important for recycling
    this.data.forEach((data) => {
      data.selected = false;
    });
    item.selected = true;

    // Send selection on higher level
    this.publish('selected', item);
  }

  /**
   * Slide down list animation
   */
  private async animateSlideDown() {
    return animate(this.itemsList.getView())
      .commit()
      .then(() => {
        this.itemsList.getView().style.offsetY = 0;
      })
      .then(
        { offsetY: this.itemsList.getView().style.height + 20 },
        animDuration,
        animate.easeOutCubic,
      )
      .then(() => {
        this.itemsList.getView().hide();
      })
      .toPromise();
  }

  /**
   * Slide up list animation
   */
  private async animateSlideUp() {
    return animate(this.itemsList.getView())
      .commit()
      .then(() => {
        this.itemsList.getView().style.offsetY =
          this.itemsList.getView().style.height + 20;
      })
      .then(() => {
        this.itemsList.getView().show();
      })
      .then({ offsetY: 0 }, animDuration, animate.easeInCubic)
      .toPromise();
  }

  /**
   * Show shadow
   */
  private async animateShowShadow() {
    return animate(this.shadow)
      .commit()
      .then(() => {
        this.shadow.style.opacity = 0;
        this.shadow.show();
      })
      .then({ opacity: 1 }, animDuration, animate.easeInCubic)
      .toPromise();
  }

  /**
   * Hide shadow
   */
  private async animateHideShadow() {
    return animate(this.shadow)
      .commit()
      .then(() => {
        this.shadow.style.opacity = 1;
      })
      .then({ opacity: 0 }, animDuration, animate.easeOutCubic)
      .then(() => {
        this.shadow.hide();
      })
      .toPromise();
  }
}

/**
 * Decoration item view for list representation
 */
class DecorationItemView extends View {
  // Icon view
  private icon: ImageView;

  // Item image
  private image: LazyImageCoverView;

  // Item button
  private button: ButtonScaleView;

  // Item description
  private description: LangBitmapFontTextView;

  // Item price
  private price: LangBitmapFontTextView;

  // Item currency icon
  private priceCurrencyIcon: ImageView;

  // Tier display
  private tier: TierView;

  // Item on click
  private onClickCallback: (
    item: DecorationListItem,
    view: DecorationItemView,
  ) => void;

  // Item new badge.
  private newBadge: ImageScaleView;

  /**
   * Constructor
   */
  constructor(
    opts: ViewOpts,
    item: DecorationListItem,
    onClick: (item: DecorationListItem, view: DecorationItemView) => void,
  ) {
    super(opts);

    const url = getDecorationPath(item.image);
    this.onClickCallback = onClick;

    this.button = new ButtonScaleView({
      superview: this,
      image: 'assets/ui/decoration/frames/cell_decor.png',
      width: opts.width,
      height: opts.height,
      tracking: {
        feature: Feature.DECORATION._,
        $subFeature: Feature.DECORATION.ITEM_SELECTED,
      },
      onClick: async () => this.onClickCallback(item, this),
    });

    this.image = new LazyImageCoverView({
      superview: this.button,
      image: url ?? 'assets/ui/decoration/icons/ico_putaway.png',
      mode: 'contain',
      verticalAlign: 'center',
      horizontalAlign: 'center',
      width: 200,
      height: 120,
      x: (opts.width! - 200) * 0.5,
      y: 10,
      spinnerColor: '#CCCCCC',
    });

    this.description = new LangBitmapFontTextView({
      superview: this.button,
      x: (opts.width! - 180) * 0.5,
      y: 168,
      width: 180,
      height: 40,
      align: 'center',
      verticalAlign: 'center',
      size: 25,
      color: '#5F9261',
      font: bitmapFonts('Body'),
      visible: false,
    });

    this.price = new LangBitmapFontTextView({
      superview: this.button,
      x: (opts.width! - 180) * 0.5,
      y: 168,
      width: 180,
      height: 40,
      align: 'center',
      verticalAlign: 'center',
      size: 25,
      color: '#9A7171',
      font: bitmapFonts('Body'),
      visible: false,
    });

    this.priceCurrencyIcon = new ImageView({
      superview: this.price,
      image: this.getCurrencyIcon(item.currency),
      width: 57,
      height: 59,
      x: 143,
      y: -7,
    });

    this.icon = new ImageView({
      superview: this.button,
      image: 'assets/ui/decoration/icons/ico_current.png',
      width: 60,
      height: 60,
      x: 180,
      y: 1,
      centerAnchor: true,
      visible: item.selected,
    });

    this.tier = new TierView({
      superview: this.button,
      x: this.description.style.x,
      y: this.description.style.y,
      width: 180,
      height: 40,
      star: {
        width: 45,
        height: 46,
      },
      visible: false,
    });

    this.newBadge = new ImageScaleView({
      superview: this.button,
      width: 71,
      height: 32,
      x: -1,
      y: 7,
      image: `assets/ui/encyclopedia/banner/notifiaction_ribbon.png`,
      visible: false,
    });

    new LangBitmapFontTextView({
      superview: this.newBadge,
      x: 10,
      width: 45,
      height: this.newBadge.style.height,
      align: 'center',
      verticalAlign: 'center',
      font: bitmapFonts('Body'),
      localeText: () => 'NEW',
      size: 21,
    });

    this.setDescription(item);
  }

  /**
   * Update item data
   */
  public setData(item: DecorationListItem) {
    const url = item.image
      ? getDecorationPath(item.image)
      : 'assets/ui/decoration/icons/ico_putaway.png';

    this.setDescription(item);

    this.button.onClick = async () => this.onClickCallback(item, this);
    this.icon.style.visible = item.selected ?? false;
    this.icon.style.scale = 1;

    // Lazy loading view
    if (url) {
      void this.image.updateImage(url);
    }

    // New badge
    this.newBadge.updateOpts({
      visible: !!item.isNew,
    });
  }

  /**
   * Update item price and description
   */
  private setDescription(item: DecorationListItem) {
    // Free or empty view or already purchased
    if (item.price === 0 || !item.image || isDecorationPurchased(item.id)) {
      if (item.stars) {
        this.description.hide();
        this.tier.getView().show();
        this.tier.setTier(item.stars, item.stars);
        return;
      }

      this.tier.getView().hide();
      this.description.localeText = () =>
        item.image ? i18n('decoration.owned') : i18n('decoration.hide');
      this.description.show();
      this.price.hide();
      return;
    }

    // Paid item
    if (item.price > 0 && item.image) {
      this.tier.getView().hide();
      this.description.hide();
      this.priceCurrencyIcon.setImage(this.getCurrencyIcon(item.currency));
      this.price.localeText = () => parseAmount(item.price);
      this.price.show();
    }
  }

  /**
   * Add selected icon
   */
  public setActiveIcon(visible: boolean) {
    if (visible) {
      animate(this.icon)
        .commit()
        .then({ scale: 0 }, 0)
        .then(() => {
          this.icon.style.visible = visible;
        })
        .then({ scale: 1 }, animDuration, animate.easeInCubic);
    } else {
      animate(this.icon)
        .commit()
        .then({ scale: 1 }, 0)
        .then({ scale: 0 }, animDuration, animate.easeInCubic)
        .then(() => {
          this.icon.style.visible = visible;
        });
    }
  }

  /**
   * Return currency icon
   */
  private getCurrencyIcon(currency: 'bPoints' | 'gems') {
    switch (currency) {
      case 'gems':
        return 'assets/ui/hud/icons/hud_gem.png';
      case 'bPoints':
        return 'assets/ui/hud/icons/hud_coin.png';
      default:
        assertNever(currency);
    }
  }
}
